import type { ExperienceOpening } from '../../utils/experiences';
import type { PackDetails } from '../../utils/packs';
import type { RoomOpeningInventory } from '../../utils/rooms';

export type CommonStatusVariant =
  'BOOKABLE' | 'MISSING_STOCK' | 'PAST_SALES' | 'NOT_ON_SALE_YET';

export type RoomStatusVariant =
  | CommonStatusVariant
  | 'MISSING_PRICE'
  | 'MISSING_DISCOUNT_PRICE'
  | 'DISCOUNT_PRICE_TOO_LOW'
  | 'DISCOUNT_OVER_MAX'
  | 'CLOSED_ON_CM'
  | 'CLOSED_BY_STAYCATION'
  | 'STOP_SELL';

export type ExperienceStatusVariant =
  | CommonStatusVariant
  | 'STOCK_CLOSED'
  | 'CLOSING_PERIOD'
  | 'CLOSED_BEFORE_CHECKIN'
  | 'LAST_MINUTE_RULE'
  | 'DAY_CLOSED'
  | 'MANUALLY_CLOSED';

export type PackStatusVariant =
  CommonStatusVariant | 'MAX_BOOKING_REACHED' | 'MISSING_ROOM_STOCK';

export type StatusVariant =
  RoomStatusVariant | ExperienceStatusVariant | PackStatusVariant;

export type StatusInfo = {
  variant: StatusVariant;
  label: string;
  cellClassName: string;
  columnClassName: string;
  isColumnGrayed: boolean;
};

export type InventoryValue =
  RoomOpeningInventory | ExperienceOpening | PackDetails;

type MessageConfig = Record<string, StatusInfo>;

const STATUS_CONFIG: MessageConfig = {
  // Sales window
  PAST_SALES: {
    variant: 'PAST_SALES',
    label: 'Past sales',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },
  FUTURE_SALES: {
    variant: 'NOT_ON_SALE_YET',
    label: 'Not on sale yet',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },

  // Room specific - CLOSED status
  CLOSED_ON_CM: {
    variant: 'CLOSED_ON_CM',
    label: 'Closed on channel manager',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },

  // Room & Pack specific - CLOSED status
  STOP_SELL: {
    variant: 'STOP_SELL',
    label: 'Stop sell applied',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },

  // Experience specific - CLOSED status
  CLOSING_PERIOD: {
    variant: 'CLOSING_PERIOD',
    label: 'Closing period activated',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },
  CLOSED_BEFORE_CHECKIN: {
    variant: 'CLOSED_BEFORE_CHECKIN',
    label: 'Last minute stock rule activated',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },
  STOCK_CLOSED: {
    variant: 'DAY_CLOSED',
    label: 'Always closed on this day of the week',
    cellClassName: 'gray-cell',
    columnClassName: 'gray-column',
    isColumnGrayed: true,
  },

  // UNPUBLISHED statuses
  MISSING_STOCK: {
    variant: 'MISSING_STOCK',
    label: 'Missing stock',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  MISSING_ROOM_STOCK: {
    variant: 'MISSING_ROOM_STOCK',
    label: 'No rooms on sale',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  MISSING_EXPERIENCES_STOCK: {
    variant: 'MISSING_STOCK',
    label: 'Missing stock on activities',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  MISSING_PRICE: {
    variant: 'MISSING_PRICE',
    label: 'Missing BAR',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  MISSING_DISCOUNT_PRICE: {
    variant: 'MISSING_DISCOUNT_PRICE',
    label: 'Missing rate',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  DISCOUNT_PRICE_TOO_LOW: {
    variant: 'DISCOUNT_PRICE_TOO_LOW',
    label: 'Rate is greater than BAR',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  DISCOUNT_OVER_MAX: {
    variant: 'DISCOUNT_OVER_MAX',
    label: 'Discount is too high',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  MANUALLY_CLOSED: {
    variant: 'MANUALLY_CLOSED',
    label: 'Manually closed',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },
  MAX_BOOKING_REACHED: {
    variant: 'MAX_BOOKING_REACHED',
    label: 'Max booking reached',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: false,
  },

  CLOSED_BY_STAYCATION: {
    variant: 'CLOSED_BY_STAYCATION',
    label: 'Rejected by account manager',
    cellClassName: 'red-cell',
    columnClassName: 'red-column',
    isColumnGrayed: true,
  },
};

export const getStatusInfoByMessage = (message: string): StatusInfo =>
  STATUS_CONFIG[message];

export const getStatusInfo = (opening: InventoryValue): StatusInfo => {
  const message = 'message' in opening ? opening.message : undefined;
  const inventoryStatus =
    'inventoryStatus' in opening ? opening.inventoryStatus : undefined;
  const isClosed = 'isClosed' in opening ? opening.isClosed : undefined;

  if (message && message in STATUS_CONFIG) {
    return STATUS_CONFIG[message];
  }

  if (inventoryStatus === 'CLOSED' && isClosed === true) {
    return STATUS_CONFIG.STOCK_CLOSED;
  }

  return {
    variant: 'BOOKABLE',
    label: 'Bookable',
    cellClassName: 'green-cell',
    columnClassName: 'green-column',
    isColumnGrayed: false,
  };
};
